// src/pages/leads/LeadActionsMenu.jsx
import React from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

/**
 * LeadActionsMenu
 *
 * Pure UI for the per-row "Actions" dropdown.
 * All business logic stays in the parent and is passed via callbacks.
 *
 * Props:
 * - lead: object (the current lead row)
 * - disabledAll: boolean (when true, disable ALL actions while appointment is in progress)
 * - onOpen: () => void                   // NEW: open Admin Sales Report for this lead
 * - onEditCustomer: () => void
 * - onAssign: () => void
 * - onUnassign: () => void
 * - onOpenDnc: () => void
 * - onOpenCancelAppt: () => void
 * - onOpenCancelSale: () => void
 * - onReinstate: () => void
 * - canCancelAppt: boolean
 * - canCancelSale: boolean
 * - canReinstate: boolean
 */
export default function LeadActionsMenu({
  lead,
  disabledAll = false,
  onOpen,                // NEW
  onEditCustomer,
  onAssign,
  onUnassign,
  onOpenDnc,
  onOpenCancelAppt,
  onOpenCancelSale,
  onReinstate,
  canCancelAppt,
  canCancelSale,
  canReinstate,
}) {
  const isAssigned = Boolean(lead?.salespersonId);

  return (
    <Menu>
      <MenuButton
        as={IconButton}
        icon={<ChevronDownIcon />}
        size="sm"
        variant="outline"
        aria-label="Lead actions"
        isDisabled={disabledAll}
        title={disabledAll ? 'In progress — actions available after Finish' : 'Lead actions'}
      />
      <MenuList>
        {/* Open Admin Sales Report (new) */}
        <MenuItem onClick={onOpen}>Open</MenuItem>

        {/* Edit customer details */}
        <MenuItem onClick={onEditCustomer}>Edit Cust</MenuItem>

        {/* Assign / Reassign */}
        <MenuItem onClick={onAssign}>{isAssigned ? 'Reassign' : 'Assign'}</MenuItem>

        {/* Unassign (only when currently assigned) */}
        {isAssigned && <MenuItem onClick={onUnassign}>Unassign</MenuItem>}

        {/* Mark as DNC */}
        <MenuItem onClick={onOpenDnc}>Mark as Dead (DNC)</MenuItem>

        {/* Cancellations */}
        {canCancelAppt && (
          <MenuItem onClick={onOpenCancelAppt}>Cancel Appointment</MenuItem>
        )}
        {canCancelSale && (
          <MenuItem onClick={onOpenCancelSale}>Cancel Sale…</MenuItem>
        )}

        {/* Reinstate (legacy rows only, if allowed) */}
        {canReinstate && <MenuItem onClick={onReinstate}>Reinstate Lead</MenuItem>}
      </MenuList>
    </Menu>
  );
}
